import i18next from "i18next";
import React, { useState, useEffect, useMemo } from "react";
import { StatusBadge } from "../atoms/StatusBadge";
import {
  HiSparkles,
  HiUsers,
  HiArrowRight,
  HiCurrencyDollar,
} from "react-icons/hi";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import HttpService from "../../services/http";
import { PaymentStatusEnum } from "../../services/paymentModule";
import { formatPayModDate } from "../../services/planChange";
import UserAvatar from "../atoms/UserAvatar";
import { CustomButton } from "../atoms/CustomButton";
import AppointmentQueryView, {
  AppointmentQueryResult,
} from "../pages/dashboard/agenda/AiAppointmentResults";
import { getStatusDisplay } from "../pages/dashboard/users/paymentHistoryDisplay";

type AggregateResult = {
  type: "aggregate";
  count: number;
  page?: any[];
  key?: string;
  path?: string;
  operator?: string;
  value?: string | number | Array<string | number> | null;
  scoped_user?: boolean;
  field_values?: string[];
};
type DistinctValuesResult = {
  type: "distinct_values";
  key?: string;
  path?: string;
  values?: Array<{ value: string; count: number }>;
};
type PaymentRow = {
  payment_identification?: string;
  user_identification?: string;
  current_status?: string;
  status?: string;
  plan_code?: string;
  product_name?: string;
  plan_amount?: number;
  plan_currency?: string;
  recurrency?: string;
  payment_date?: string;
  next_payment_date?: string;
  [k: string]: any;
};
type PaymentUserGroup = {
  user_identification: string;
  count: number;
  payments: PaymentRow[];
  exists?: boolean;
  first_name?: string | null;
  last_name?: string | null;
  avatar?: string | null;
  email?: string | null;
};
type PaymentQueryResult = {
  type: "payment_query";
  payment_filters?: Record<string, any>;
  payments?: PaymentRow[];
  total?: number;
  users?: PaymentUserGroup[];
  distinct_user_count?: number;
  error?: string;
};
type LookupResult = {
  type: "lookup";
  hasContext?: boolean;
  context?: string;
  answer?: string;
  sources?: Array<{
    doc_id: string;
    filename?: string | null;
    chunk_index?: number;
  }>;
  note?: string;
  reason?: "no_dimensions" | "no_active_dimension" | "no_documents";
};
type PlatformFeatureResult = {
  type: "platform_feature";
  feature?: string | null;
};
type OutOfScopeResult = {
  type: "out_of_scope";
};
type SearchResult =
  | AggregateResult
  | DistinctValuesResult
  | LookupResult
  | PlatformFeatureResult
  | PaymentQueryResult
  | AppointmentQueryResult
  | OutOfScopeResult;

const KNOWN_TYPES = [
  "aggregate",
  "distinct_values",
  "lookup",
  "platform_feature",
  "payment_query",
  "appointment_query",
  "out_of_scope",
];

interface AskAiSearchProps {
  userIdentification?: string;
  compact?: boolean;
  locked?: boolean;
  onUnlock?: () => void;
}

const AskAiSearch: React.FC<AskAiSearchProps> = ({
  userIdentification,
  compact = false,
  locked = false,
  onUnlock,
}) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { customer_identification, user } = useAuth();
  const entityIdentification = user?.entity_identification;

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [lockedHit, setLockedHit] = useState(false);
  const [focused, setFocused] = useState(false);

  const rotatingExamples = useMemo(() => {
    const list = t("dashboard.askAi.placeholderExamples", {
      returnObjects: true,
    });
    return Array.isArray(list) ? (list as string[]).filter(Boolean) : [];
  }, [t]);

  const staticPlaceholder = (
    userIdentification
      ? t("dashboard.askAi.placeholderUser")
      : t("dashboard.askAi.placeholderEntity")
  ) as string;

  const animatePlaceholder =
    !compact && !userIdentification && rotatingExamples.length > 0;

  const [typed, setTyped] = useState(staticPlaceholder);

  useEffect(() => {
    if (!animatePlaceholder || query.trim() || focused) {
      setTyped(staticPlaceholder);
      return;
    }
    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setTyped(rotatingExamples[0]);
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    let exampleIdx = 0;
    let charCount = 0;
    let phase: "typing" | "holding" | "erasing" = "typing";

    const step = () => {
      const phrase = rotatingExamples[exampleIdx] ?? "";
      if (phase === "typing") {
        charCount++;
        setTyped(phrase.slice(0, charCount));
        if (charCount >= phrase.length) {
          phase = "holding";
          timer = setTimeout(step, 1800);
        } else {
          timer = setTimeout(step, 55);
        }
      } else if (phase === "holding") {
        phase = "erasing";
        timer = setTimeout(step, 30);
      } else {
        charCount--;
        setTyped(phrase.slice(0, Math.max(charCount, 0)));
        if (charCount <= 0) {
          exampleIdx = (exampleIdx + 1) % rotatingExamples.length;
          phase = "typing";
          timer = setTimeout(step, 400);
        } else {
          timer = setTimeout(step, 30);
        }
      }
    };

    setTyped("");
    timer = setTimeout(step, 400);
    return () => clearTimeout(timer);
  }, [animatePlaceholder, query, focused, staticPlaceholder, rotatingExamples]);

  const displayedPlaceholder = animatePlaceholder ? typed : staticPlaceholder;

  const runSearch = async (q?: string) => {
    const question = (q ?? query).trim();
    if (!question) return;
    if (locked) {
      setResult(null);
      setLockedHit(true);
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const base = `${customer_identification}/${entityIdentification}/user`;
      const url = userIdentification
        ? `${base}/${userIdentification}/ai-search`
        : `${base}/ai-search`;
      const res = await HttpService.post<{ element: SearchResult }>(url, {
        query: question,
        include_sources: true,
        limit: 20,
      });
      const payload = res.data?.element ?? null;
      if (!payload || !KNOWN_TYPES.includes(payload.type)) {
        toast.error(t("dashboard.askAi.errorGeneric") as string);
      } else {
        setResult(payload);
      }
    } catch (err: any) {
      toast.error(
        (err?.response?.data?.message as string) ||
          (t("dashboard.askAi.errorGeneric") as string)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={compact ? "" : "mb-6"}>
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
        <HiSparkles
          className="text-blue-600 shrink-0"
          size={compact ? 18 : 22}
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => e.key === "Enter" && runSearch()}
          aria-label={staticPlaceholder}
          placeholder={displayedPlaceholder}
          className="flex-1 min-w-0 border-0 focus:ring-0 text-gray-800 placeholder-gray-400 bg-transparent text-sm md:text-base p-0"
        />
        <CustomButton
          type="button"
          variant="none"
          size="icon"
          onClick={() => runSearch()}
          disabled={loading || !query.trim()}
          aria-label={t("dashboard.askAi.ask") as string}
          className="shrink-0 text-blue-600 transition-colors enabled:hover:text-blue-700 disabled:text-gray-300"
          icon={
            loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
            ) : (
              <HiArrowRight size={compact ? 16 : 18} />
            )
          }
        />
      </div>

      {!locked && (
        <p className="mt-1 text-left text-xs text-gray-400">
          {t("dashboard.askAi.disclaimer")}
        </p>
      )}

      {locked && lockedHit && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2 mb-2 text-blue-600">
            <HiSparkles size={16} className="shrink-0" />
            <span className="text-xs font-semibold">
              {t("dashboard.askAi.lockedAssistant")}
            </span>
          </div>
          <p className="text-sm text-gray-800 leading-relaxed">
            <Trans
              t={t}
              i18nKey="dashboard.askAi.lockedMessage"
              components={{
                cta: (
                  <button
                    type="button"
                    onClick={onUnlock}
                    className="font-medium text-blue-600 underline hover:text-blue-700"
                  />
                ),
              }}
            />
          </p>
        </div>
      )}

      {result && (
        <div className="mt-3">
          {result.type === "aggregate" ? (
            <AggregateView
              result={result}
              navigate={navigate}
              t={t}
              scopedToUser={!!userIdentification}
            />
          ) : result.type === "distinct_values" ? (
            <DistinctValuesView result={result} t={t} />
          ) : result.type === "payment_query" ? (
            <PaymentQueryView
              result={result}
              navigate={navigate}
              t={t}
              scopedToUser={!!userIdentification}
            />
          ) : result.type === "appointment_query" ? (
            <AppointmentQueryView result={result} />
          ) : result.type === "platform_feature" ? (
            <PlatformFeatureView result={result} t={t} />
          ) : result.type === "out_of_scope" ? (
            <OutOfScopeView t={t} />
          ) : (
            <LookupView
              result={result}
              navigate={navigate}
              t={t}
              scopedToUser={!!userIdentification}
            />
          )}
        </div>
      )}
    </div>
  );
};

const NATIVE_VALUE_FIELD: Record<string, string> = {
  "user.first_name": "user_first_name",
  "user.last_name": "user_last_name",
  "user.email": "user_email",
  "user.username": "user_user_name",
};

const DATE_FORMATTERS: Record<string, (v: string) => string> = {
  "user.created_at": (v) => moment.utc(v).local().format("lll"),
  "user.last_connection": (v) => moment.utc(v).local().format("lll"),
  "user.birth_date": (v) => moment.utc(v).format("LL"),
};

const formatValueForPath = (value: string, path?: string): string => {
  const fmt = path ? DATE_FORMATTERS[path] : undefined;
  if (!fmt) return value;
  const m = moment(value);
  if (!m.isValid()) return value;
  return fmt(value);
};

const AggregateView: React.FC<{
  result: AggregateResult;
  navigate: any;
  t: any;
  scopedToUser?: boolean;
}> = ({ result, navigate, t, scopedToUser }) => {
  const page = result.page || [];

  if (scopedToUser || result.scoped_user) {
    if (result.count === 0) {
      const hadCriterion =
        result.value != null &&
        result.operator != null &&
        result.operator !== "not_null" &&
        result.operator !== "null";
      return (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-4 text-center text-sm text-gray-600">
          {hadCriterion
            ? t("dashboard.askAi.noMatchForUser")
            : t("dashboard.askAi.userNoValue")}
        </div>
      );
    }
    const fieldValues = (result.field_values || []).filter(
      (v) => v != null && String(v).trim() !== ""
    );
    if (fieldValues.length > 0) {
      const shown = fieldValues
        .map((v) => formatValueForPath(String(v), result.path))
        .join(", ");
      return (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-4">
          <div className="text-sm text-gray-800">{shown}</div>
        </div>
      );
    }
    const u = page[0];
    const nativeField = result.path
      ? NATIVE_VALUE_FIELD[result.path]
      : undefined;
    const nativeValue =
      u && nativeField
        ? u[nativeField]
        : u &&
          NATIVE_VALUE_FIELD[result.path || ""] === undefined &&
          result.path &&
          result.path.startsWith("user.")
        ? `${u.user_first_name || ""} ${u.user_last_name || ""}`.trim() ||
          u.user_email
        : undefined;
    if (nativeValue) {
      return (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-4">
          <div className="text-sm text-gray-800">{nativeValue}</div>
        </div>
      );
    }
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm text-gray-600">
        {t("dashboard.askAi.userNoValue")}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <HiUsers className="text-blue-600" size={20} />
        <div>
          <div className="text-xl font-bold text-gray-900 leading-none">
            {result.count}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {t("dashboard.askAi.resultsFound")}
          </div>
        </div>
      </div>
      {page.length > 0 ? (
        <ul className="divide-y divide-gray-100">
          {page.map((u: any, i: number) => {
            const isString = typeof u === "string";
            const id = isString ? u : u.user_identification || u.doc_id || u.id;
            const primary = isString
              ? id
              : u.user_email || u.user_user_name || u.filename || id;
            const secondary =
              !isString && (u.user_first_name || u.user_last_name)
                ? `${u.user_first_name || ""} ${u.user_last_name || ""}`.trim()
                : null;
            return (
              <li key={id || i}>
                <button
                  type="button"
                  onClick={() => id && navigate(`/control-panel/users/${id}`)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition"
                >
                  <span className="min-w-0 flex flex-col">
                    <span className="text-sm text-gray-700 truncate">
                      {primary}
                    </span>
                    {secondary && (
                      <span className="text-xs text-gray-400 truncate">
                        {secondary}
                      </span>
                    )}
                  </span>
                  <HiArrowRight className="text-gray-300 shrink-0" size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="px-4 py-6 text-center text-sm text-gray-400">
          {t("dashboard.askAi.noResults")}
        </div>
      )}
      {result.count > page.length && page.length > 0 && (
        <div className="px-4 py-2.5 border-t border-gray-100 text-center text-xs text-gray-500">
          <span>
            {t("dashboard.askAi.showingOfTotal", {
              shown: page.length,
              total: result.count,
            })}
            <span className="text-gray-400">
              {" "}
              · {t("dashboard.askAi.refineHint")}
            </span>
          </span>
          <button
            type="button"
            onClick={() => navigate("/control-panel/advanced-search")}
            className="mt-1 mx-auto flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
          >
            {t("dashboard.askAi.openAdvancedSearch")}
            <HiArrowRight size={12} className="shrink-0" />
          </button>
        </div>
      )}
    </div>
  );
};

const DistinctValuesView: React.FC<{
  result: DistinctValuesResult;
  t: any;
}> = ({ result, t }) => {
  const values = result.values || [];
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <HiSparkles className="text-blue-600" size={20} />
        <div>
          <div className="text-xl font-bold text-gray-900 leading-none">
            {values.length}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {t("dashboard.askAi.distinctFound")}
          </div>
        </div>
      </div>
      {values.length > 0 ? (
        <ul className="divide-y divide-gray-100">
          {values.map((v, i) => (
            <li
              key={`${v.value}-${i}`}
              className="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <span className="text-sm text-gray-700 truncate">{v.value}</span>
              <span className="shrink-0 inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 text-xs font-medium">
                {v.count}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-4 py-6 text-center text-sm text-gray-400">
          {t("dashboard.askAi.noResults")}
        </div>
      )}
    </div>
  );
};

const PlatformFeatureView: React.FC<{
  result: PlatformFeatureResult;
  t: any;
}> = ({ result, t }) => {
  const examples = [
    t("dashboard.askAi.suggestionA"),
    t("dashboard.askAi.suggestionB"),
  ].filter(Boolean) as string[];
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-4">
      <div className="flex items-start gap-3">
        <HiSparkles className="text-blue-500 mt-0.5 shrink-0" size={20} />
        <div className="min-w-0">
          <p className="text-sm text-gray-700">
            {result.feature
              ? t("dashboard.askAi.platformFeatureNamed", {
                  feature: result.feature,
                })
              : t("dashboard.askAi.platformFeature")}
          </p>
          {examples.length > 0 && (
            <div className="mt-2">
              <span className="text-xs text-gray-500">
                {t("dashboard.askAi.tryInstead")}{" "}
              </span>
              <span className="text-xs text-blue-700">
                {examples.map((e, i) => (
                  <span key={i}>
                    “{e}”{i < examples.length - 1 ? " · " : ""}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OutOfScopeView: React.FC<{ t: any }> = ({ t }) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
      <div className="flex items-start gap-3">
        <HiSparkles className="text-gray-400 mt-0.5 shrink-0" size={20} />
        <p className="text-sm text-gray-600">
          {t("dashboard.askAi.outOfScope")}
        </p>
      </div>
    </div>
  );
};

const PaymentQueryView: React.FC<{
  result: PaymentQueryResult;
  navigate: any;
  t: any;
  scopedToUser?: boolean;
}> = ({ result, navigate, t, scopedToUser = false }) => {
  if (result.error) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {t("dashboard.askAi.paymentError")}
      </div>
    );
  }

  const rows = result.payments || [];
  const groups: PaymentUserGroup[] =
    result.users ||
    Object.values(
      rows.reduce((acc: Record<string, PaymentUserGroup>, p) => {
        const uid = p.user_identification || "—";
        (acc[uid] = acc[uid] || {
          user_identification: uid,
          count: 0,
          payments: [],
        }).count += 1;
        acc[uid].payments.push(p);
        return acc;
      }, {})
    );
  const clientCount = result.distinct_user_count ?? groups.length;
  const stepCount = result.total ?? rows.length;
  const partial = stepCount > rows.length && rows.length > 0;

  const recurrencySuffix = (recurrency?: string) => {
    const key = (recurrency || "").toUpperCase();
    if (!key || key === "UNIQUE") return "";
    const map: Record<string, string> = {
      MONTHLY: "dashboard.askAi.perMonth",
      ANNUALLY: "dashboard.askAi.perYear",
      WEEKLY: "dashboard.askAi.perWeek",
      DAILY: "dashboard.askAi.perDay",
    };
    return map[key] ? ` ${t(map[key])}` : "";
  };
  const fmtAmount = (p: PaymentRow) => {
    if (typeof p.plan_amount !== "number") return "";
    let formatted: string;
    try {
      formatted = new Intl.NumberFormat(i18next.language || "en", {
        style: "currency",
        currency: p.plan_currency || "USD",
      }).format(p.plan_amount / 100);
    } catch {
      formatted = `${(p.plan_amount / 100).toFixed(2)}${
        p.plan_currency ? " " + p.plan_currency : ""
      }`;
    }
    return `${formatted}${recurrencySuffix(p.recurrency)}`;
  };

  const displayName = (g: PaymentUserGroup) => {
    const full = [g.first_name, g.last_name].filter(Boolean).join(" ").trim();
    return full || g.email || g.user_identification;
  };

  const renderStep = (p: PaymentRow, key: React.Key) => {
    const amount = fmtAmount(p);
    const product = p.product_name || p.plan_code;
    const date = formatPayModDate(p.payment_date, "lll");
    const status = p.current_status || p.status;
    const statusDisplay =
      status && getStatusDisplay(status as PaymentStatusEnum, t);
    return (
      <li
        key={key}
        className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500"
      >
        <span className="flex min-w-0 flex-1 basis-64">
          {product && <span className="min-w-0 truncate">{product}</span>}
          {date && (
            <span className="shrink-0 whitespace-pre">
              {product ? ` · ${date}` : date}
            </span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          {amount && <span className="text-gray-600">{amount}</span>}
          {statusDisplay && (
            <StatusBadge color={statusDisplay.tone} size="xs">
              {statusDisplay.label}
            </StatusBadge>
          )}
        </span>
      </li>
    );
  };

  const renderPartialNotice = (sentence: string) => (
    <div className="px-4 py-2.5 border-t border-gray-100 text-center text-xs text-gray-500">
      <span>
        {sentence}
        <span className="text-gray-400">
          {" "}
          · {t("dashboard.askAi.refineHint")}
        </span>
      </span>
      <button
        type="button"
        onClick={() => navigate("/control-panel/advanced-search")}
        className="mt-1 mx-auto flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
      >
        {t("dashboard.askAi.openAdvancedSearch")}
        <HiArrowRight size={12} className="shrink-0" />
      </button>
    </div>
  );

  if (scopedToUser) {
    const userRows =
      result.users && result.users.length ? result.users[0].payments : rows;
    return (
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
          <HiCurrencyDollar className="text-blue-600" size={20} />
          <div className="text-sm font-semibold text-gray-800">
            {t("dashboard.askAi.paymentUserTitle")}
          </div>
        </div>
        {userRows.length > 0 ? (
          <ul className="px-4 py-2.5 space-y-2 sm:space-y-1">
            {userRows.map((p, i) =>
              renderStep(p, p.payment_identification || i)
            )}
          </ul>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            {t("dashboard.askAi.paymentNoneForUser")}
          </div>
        )}
        {partial &&
          renderPartialNotice(
            t("dashboard.askAi.showingUserPaymentsOfTotal", {
              shown: rows.length,
              total: stepCount,
            })
          )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <HiCurrencyDollar className="text-blue-600 shrink-0" size={20} />
        <div>
          <div className="text-xl font-bold text-gray-900 leading-none">
            {clientCount}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {partial
              ? t("dashboard.askAi.paymentClientsInShown", {
                  count: clientCount,
                  shown: rows.length,
                })
              : t("dashboard.askAi.paymentClientsFound", {
                  count: clientCount,
                })}
            {stepCount !== clientCount && (
              <span className="whitespace-nowrap">
                {" "}
                · {t("dashboard.askAi.paymentStepsCount", { count: stepCount })}
              </span>
            )}
          </div>
        </div>
      </div>
      {groups.length > 0 ? (
        <ul className="divide-y divide-gray-100">
          {groups.map((g, gi) => {
            const uid = g.user_identification;
            const deleted = g.exists === false;
            const clickable = !deleted && uid && uid !== "—";
            return (
              <li key={uid || gi} className="px-4 py-2.5">
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() =>
                    clickable && navigate(`/control-panel/users/${uid}`)
                  }
                  className={`w-full flex items-center justify-between gap-3 text-left transition ${
                    clickable
                      ? "hover:opacity-80 cursor-pointer"
                      : "cursor-default"
                  }`}
                >
                  <span className="min-w-0 flex items-center gap-2">
                    <UserAvatar
                      firstName={g.first_name}
                      lastName={g.last_name}
                      email={g.email}
                      avatar={g.avatar}
                      sizeClass="w-6 h-6"
                      textClass="text-xs"
                      deleted={deleted}
                    />
                    <span
                      className={`text-sm font-medium truncate ${
                        deleted ? "text-gray-400" : "text-gray-800"
                      }`}
                    >
                      {displayName(g)}
                    </span>
                    {deleted && (
                      <StatusBadge color="gray" size="xs">
                        {t("dashboard.askAi.paymentClientDeleted")}
                      </StatusBadge>
                    )}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {g.count > 1 && (
                      <StatusBadge color="blue" size="xs">
                        {t("dashboard.askAi.paymentStepsCount", {
                          count: g.count,
                        })}
                      </StatusBadge>
                    )}
                    {clickable && (
                      <HiArrowRight
                        className="text-gray-300 shrink-0"
                        size={14}
                      />
                    )}
                  </span>
                </button>
                <ul className="mt-1.5 ml-6 space-y-2 sm:space-y-1">
                  {g.payments.map((p, pi) =>
                    renderStep(p, p.payment_identification || pi)
                  )}
                </ul>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="px-4 py-6 text-center text-sm text-gray-400">
          {t("dashboard.askAi.noResults")}
        </div>
      )}
      {partial &&
        renderPartialNotice(
          t("dashboard.askAi.showingPaymentsOfTotal", {
            shown: rows.length,
            total: stepCount,
          })
        )}
    </div>
  );
};

const LookupView: React.FC<{
  result: LookupResult;
  navigate: any;
  t: any;
  scopedToUser?: boolean;
}> = ({ result, navigate, t, scopedToUser = false }) => {
  const text = result.answer || result.context;
  if (!text && !result.hasContext) {
    const emptyMsg = scopedToUser
      ? t("dashboard.askAi.noResultForUser")
      : result.reason === "no_dimensions"
      ? t("dashboard.askAi.noDimensions")
      : result.reason === "no_active_dimension"
      ? t("dashboard.askAi.noActiveDimension")
      : result.reason === "no_documents"
      ? t("dashboard.askAi.noDocuments")
      : t("dashboard.askAi.noAnswer");
    const suggestForm = !scopedToUser && result.reason === "no_documents";
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
        {emptyMsg}
        {result.note && (
          <p className="mt-1 text-xs text-amber-600">{result.note}</p>
        )}
        {suggestForm && (
          <button
            type="button"
            onClick={() => navigate("/control-panel/advanced-search")}
            className="mt-3 mx-auto flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
          >
            {t("dashboard.askAi.tryAdvancedSearch")}
            <HiArrowRight size={12} className="shrink-0" />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
        {text}
      </div>
      {result.note && (
        <p className="mt-2 text-xs text-amber-600">{result.note}</p>
      )}
    </div>
  );
};

export default AskAiSearch;
